import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const EXPERIENCE = [
  { value: 'new', label: 'Never lifted' },
  { value: 'returning', label: 'Coming back' },
  { value: 'regular', label: 'Lift regularly' },
];
const EQUIPMENT = [
  { value: 'gym', label: 'Gym with a rack' },
  { value: 'home', label: 'Home barbell, no rack' },
];

const box = { background: '#141414', border: '1px solid #333', padding: '24px 22px', color: '#ededed' };
const legendStyle = { fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#C7FA3E', padding: '0 6px' };
const fieldset = { border: '1px solid #333', padding: '12px 14px', margin: '0 0 14px' };

const RadioGroup = ({ legend, options, value, onChange, name }) => (
  <fieldset style={fieldset}>
    <legend style={legendStyle}>{legend}</legend>
    {options.map((o) => (
      <label key={o.value} style={{ display: 'block', padding: '6px 0', fontSize: '15px', color: '#ededed', cursor: 'pointer' }}>
        <input
          type="radio"
          name={name}
          value={o.value}
          checked={value === o.value}
          onChange={() => onChange(o.value)}
          style={{ marginRight: 8, accentColor: '#C7FA3E' }}
          data-testid={`magnet-${name}-${o.value}`}
        />
        {o.label}
      </label>
    ))}
  </fieldset>
);

export const TrackPicker = ({ experience, equipment, setExperience, setEquipment }) => {
  const label = (list, v) => list.find((o) => o.value === v)?.label;
  return (
    <div style={box} data-testid="magnet-track-picker">
      <h3 className="text-white" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.5rem', letterSpacing: '0.03em', margin: '0 0 6px' }}>
        Which track am I?
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#a1a1aa' }}>
        Answer two questions and we hand you the right PDF below.
      </p>
      <RadioGroup legend="Lifting background" options={EXPERIENCE} value={experience} onChange={setExperience} name="picker-experience" />
      <RadioGroup legend="Your setup" options={EQUIPMENT} value={equipment} onChange={setEquipment} name="picker-equipment" />
      {experience && equipment && (
        <div style={{ background: '#0A0A0A', border: '1px solid #333', padding: '16px 18px' }} data-testid="magnet-picker-result">
          <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#ededed' }}>
            Your track: <strong style={{ color: '#C7FA3E' }}>{label(EXPERIENCE, experience)} · {label(EQUIPMENT, equipment)}</strong>
          </p>
          <button
            type="button"
            onClick={() => document.querySelector('.na-lead-log')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            style={{ background: '#C7FA3E', color: '#0A0A0A', fontWeight: 800, fontSize: '14px', border: 0, padding: '12px 20px', cursor: 'pointer' }}
            data-testid="magnet-picker-cta"
          >
            Get this PDF — instant download + inbox copy
          </button>
        </div>
      )}
    </div>
  );
};

const getUtm = (key) => new URLSearchParams(window.location.search).get(key) || '';

export const LeadMagnetForm = ({ experience, equipment, setExperience, setEquipment, sourceArticle, magnet = 'barbell-12-week' }) => {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const lastSubmit = useRef(0);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (Date.now() - lastSubmit.current < 10000) return;
    if (!experience || !equipment) { setError('Pick your lifting background and setup first.'); return; }
    if (!email.includes('@')) { setError('Enter a valid email address.'); return; }
    lastSubmit.current = Date.now();
    setPending(true);
    try {
      const res = await fetch(`${API}/api/leads/magnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, magnet, experience, equipment,
          source_article: sourceArticle || '',
          utm_source: getUtm('utm_source'), utm_medium: getUtm('utm_medium'),
          utm_campaign: getUtm('utm_campaign'), utm_content: getUtm('utm_content'),
          website: hp,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      try {
        const a = document.createElement('a');
        a.href = data.pdf_url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        window.open(data.pdf_url, '_blank');
      }
      if (window.gtag) window.gtag('event', 'generate_lead', { magnet, track: `${experience}-${equipment}` });
      setDone(data);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Something went wrong. Try again.');
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <div style={box} data-testid="magnet-form-success">
        <p style={{ margin: '0 0 10px', fontSize: '15px', color: '#ededed', lineHeight: 1.6 }}>
          <strong style={{ color: '#C7FA3E' }}>Sent.</strong> Your PDF is downloading and a copy is in your inbox
          (check spam if it takes more than a minute). Log every session.
        </p>
        <a href={done.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: '#C7FA3E', fontSize: '14px', fontWeight: 700 }} data-testid="magnet-download-again">
          Download again
        </a>
      </div>
    );
  }

  return (
    <div style={box} data-testid="magnet-lead-form">
      <h3 className="text-white" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.5rem', letterSpacing: '0.03em', margin: '0 0 6px' }}>
        Get the 12-Week Barbell Starter PDF
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#a1a1aa', lineHeight: 1.6 }}>
        Pick your track, drop your email, and the plan opens now and lands in your inbox. One short check-in a week
        for 12 weeks after that. Unsubscribe any time.
      </p>
      <form onSubmit={submit}>
        <RadioGroup legend="Lifting background" options={EXPERIENCE} value={experience} onChange={setExperience} name="form-experience" />
        <RadioGroup legend="Your setup" options={EQUIPMENT} value={equipment} onChange={setEquipment} name="form-equipment" />
        <input
          type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          tabIndex={-1} autoComplete="off" aria-hidden="true"
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'stretch' }}>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={{ flex: '1 1 240px', padding: '12px 14px', border: '1px solid #333', background: '#0A0A0A', color: '#ededed', fontSize: '15px' }}
            data-testid="magnet-email-input"
          />
          <button
            type="submit" disabled={pending}
            style={{ background: '#C7FA3E', color: '#0A0A0A', fontWeight: 800, fontSize: '14px', border: 0, padding: '12px 22px', cursor: pending ? 'wait' : 'pointer', opacity: pending ? 0.7 : 1 }}
            data-testid="magnet-submit-btn"
          >
            {pending ? 'Sending…' : 'Send me the plan'}
          </button>
        </div>
        {error && <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#E63946' }} data-testid="magnet-form-error">{error}</p>}
        <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#71717a' }}>
          One email a week. Unsubscribe with one click. We never sell or share your address.
        </p>
      </form>
    </div>
  );
};

// Mounts the React picker + form into the article's .na-track-picker / .na-lead-log
// containers (the CSS-only markup inside them is the no-JS fallback).
export const LeadMagnetMount = ({ article }) => {
  const [nodes, setNodes] = useState(null);
  const [experience, setExperience] = useState('');
  const [equipment, setEquipment] = useState('');

  useEffect(() => {
    if (!article?.content?.includes('na-lead-log')) { setNodes(null); return; }
    const picker = document.querySelector('.article-content .na-track-picker');
    const log = document.querySelector('.article-content .na-lead-log');
    if (picker) picker.innerHTML = '';
    if (log) log.innerHTML = '';
    setNodes({ picker, log });
  }, [article?.content]);

  if (!nodes) return null;
  return (
    <>
      {nodes.picker && createPortal(
        <TrackPicker experience={experience} equipment={equipment} setExperience={setExperience} setEquipment={setEquipment} />,
        nodes.picker,
      )}
      {nodes.log && createPortal(
        <LeadMagnetForm
          experience={experience} equipment={equipment}
          setExperience={setExperience} setEquipment={setEquipment}
          sourceArticle={article?.slug}
        />,
        nodes.log,
      )}
    </>
  );
};
